import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Decouples a text input from expensive state updates by maintaining local state
 * and debouncing commits to the external store.
 */
export function useDebouncedValue(
  externalValue: string,
  onCommit: (value: string) => void,
  delay = 300
): { value: string; onChange: (value: string) => void; flush: () => void } {
  const [localValue, setLocalValue] = useState(externalValue);
  const localValueRef = useRef(localValue);
  const onCommitRef = useRef(onCommit);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDirtyRef = useRef(false);

  // Keep refs in sync
  onCommitRef.current = onCommit;
  localValueRef.current = localValue;

  // Sync external value when it changes from outside (e.g. undo/redo)
  // but only if the user isn't actively editing (no pending debounce)
  useEffect(() => {
    if (!isDirtyRef.current) {
      setLocalValue(externalValue);
    }
  }, [externalValue]);

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (isDirtyRef.current) {
      isDirtyRef.current = false;
      onCommitRef.current(localValueRef.current);
    }
  }, []);

  const onChange = useCallback(
    (value: string) => {
      setLocalValue(value);
      localValueRef.current = value;
      isDirtyRef.current = true;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        isDirtyRef.current = false;
        onCommitRef.current(value);
      }, delay);
    },
    [delay]
  );

  // Flush on unmount so no data is lost
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (isDirtyRef.current) {
        onCommitRef.current(localValueRef.current);
      }
    };
  }, []);

  return { value: localValue, onChange, flush };
}
